import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiUser, FiMail, FiPhone, FiAward, FiClock, FiMessageSquare, FiCheckCircle } from 'react-icons/fi'
import { registerVolunteer } from '../utils/api'

const InputField = ({ icon: Icon, label, ...props }) => (
  <div className="relative group">
    <label className="block text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5 sm:mb-2 group-focus-within:text-accent-maroon transition-colors">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-accent-maroon transition-colors">
        <Icon size={16} />
      </div>
      <input
        {...props}
        className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-4 bg-white/5 border border-gray-200 rounded-lg sm:rounded-2xl text-sm sm:text-base text-gray-800 placeholder-gray-500 focus:border-accent-maroon focus:bg-white/10 focus:outline-none transition-all duration-300"
      />
    </div>
  </div>
)

const VolunteerForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
    skills: '',
    availability: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState({ type: '', message: '' })

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setStatus({ type: '', message: '' })

    try {
      await registerVolunteer(formData)
      setStatus({ type: 'success', message: 'Applied successfully! Jai Hind! See you on the ground!' })
      setFormData({
        fullName: '',
        mobileNumber: '',
        email: '',
        skills: '',
        availability: '',
        message: '',
      })
      setTimeout(() => setStatus({ type: '', message: '' }), 5000)
    } catch (error) {
      const errorData = error.response?.data
      setStatus({
        type: 'error',
        message: errorData?.error || 'SOMETHING WENT WRONG. TRY AGAIN.'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <section id="volunteers" className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gray-50">
      <div className="absolute top-1/2 left-0 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-accent-maroon/5 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[250px] h-[250px] sm:w-[400px] sm:h-[400px] bg-accent-maroon/3 blur-[100px] rounded-full translate-x-1/4 translate-y-1/4" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-20 items-center">
          <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black font-grotesk tracking-tighter leading-tight mb-4 sm:mb-6 md:mb-8 text-gray-800">
              JOIN THE <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">SWACHH TEAM.</span>
            </h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg font-medium mb-8 md:mb-12 max-w-lg leading-relaxed">
              We are out on the ground cleaning the lakes and streets of Udaipur. Don't just watch from home, come and do Shramdaan with us! Join over 500+ proud local volunteers who are already working on the field!
            </p>

            <div className="space-y-4 md:space-y-8">
              {[
                { title: 'REAL IMPACT', desc: 'Directly clean our sacred lakes and protect our beautiful heritage by hand.' },
                { title: 'DESH PREM', desc: 'Work together as brothers and sisters with patriotic friends who love Udaipur.' },
                { title: 'PROUD RECOGNITION', desc: 'Get proud volunteer awards and verified physical work certificates!' }
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-6 group">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center text-accent-maroon group-hover:bg-accent-maroon group-hover:text-white transition-all duration-500">
                    <FiCheckCircle size={24} />
                  </div>
                  <div>
                    <h4 className="text-gray-800 font-black text-sm tracking-widest">{item.title}</h4>
                    <p className="text-gray-600 text-sm font-medium">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-accent-maroon to-accent-maroonDark rounded-xl sm:rounded-2xl lg:rounded-[2.5rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 rounded-xl sm:rounded-2xl lg:rounded-[2.5rem] shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 md:space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                  <InputField
                    icon={FiUser}
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter name"
                    required
                  />
                  <InputField
                    icon={FiPhone}
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    placeholder="Enter number"
                    required
                  />
                </div>

                <InputField
                  icon={FiMail}
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                  <InputField
                    icon={FiAward}
                    label="Your Skills"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    placeholder="e.g. Photography"
                  />
                  <div className="relative group">
                    <label className="block text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5 sm:mb-2 group-focus-within:text-accent-maroon transition-colors">
                      Availability
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-accent-maroon transition-colors pointer-events-none">
                        <FiClock size={16} />
                      </div>
                      <select
                        name="availability"
                        value={formData.availability}
                        onChange={handleChange}
                        className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-4 bg-white/5 border border-gray-200 rounded-lg sm:rounded-2xl text-sm sm:text-base text-gray-800 focus:border-accent-maroon focus:bg-white/10 focus:outline-none transition-all appearance-none cursor-pointer"
                        required
                      >
                        <option value="" className="bg-white text-gray-500">Select...</option>
                        <option value="weekends" className="bg-white text-gray-800">Weekends</option>
                        <option value="weekdays" className="bg-white text-gray-800">Weekdays</option>
                        <option value="anytime" className="bg-white text-gray-800">Anytime</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <label className="block text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5 sm:mb-2 group-focus-within:text-accent-maroon transition-colors">
                    Your Motivation
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 sm:left-4 top-4 text-gray-500 group-focus-within:text-accent-maroon transition-colors">
                      <FiMessageSquare size={16} />
                    </div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Why do you want to join?"
                      rows="4"
                      className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base bg-white/5 border border-gray-200 rounded-lg sm:rounded-2xl text-gray-800 placeholder-gray-500 focus:border-accent-maroon focus:bg-white/10 focus:outline-none transition-all resize-none"
                    />
                  </div>
                </div>

                <AnimatePresence>
                  {status.message && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`p-3 sm:p-4 rounded-lg sm:rounded-2xl text-center text-xs font-black tracking-widest uppercase ${
                        status.type === 'success' ? 'bg-green-500/10 text-green-600 border border-green-500/20' : 'bg-red-500/10 text-red-600 border border-red-500/20'
                      }`}
                    >
                      {status.message}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(128,0,0,0.3)' }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 sm:py-4 md:py-5 bg-gradient-to-r from-accent-maroon to-accent-maroonDark text-white font-black uppercase tracking-[0.3em] text-xs sm:text-sm rounded-lg sm:rounded-2xl transition-all shadow-xl disabled:opacity-50"
                >
                  {loading ? 'JOINING...' : 'JOIN THE MOVEMENT'}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default VolunteerForm